import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, img } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import DropCountdown from "@/components/DropCountdown";
import Marquee from "@/components/Marquee";
import DropBanner from "@/components/DropBanner";
import { Reveal, MaskedLine, Eyebrow } from "@/components/Reveal";

const CATEGORIES = [
  { name: "Tees", image: "panther-panels-tee.jpg" },
  { name: "Hoodies", image: "panther-panels-hoodie.jpg" },
  { name: "Sets", image: "panther-set.jpg" },
];

const CHAPTERS = [
  { n: "01", title: "Discipline", text: "Greatness doesn't require noise. Discipline creates progress — the work done in silence, repeated daily, is the only shortcut that exists." },
  { n: "02", title: "Vision", text: "Vision provides direction. You move different because you see different — a destination nobody else believed was real until you arrived." },
  { n: "03", title: "Loyalty", text: "Loyalty establishes character. To the people who were there before the results, and to the standard you refuse to lower." },
  { n: "04", title: "Faith", text: "Faith keeps you moving when results aren't visible yet. Built in silence. Move in faith. Leave a legacy." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <img src={img("hero-v5.jpg")} alt="Jae Noir campaign" className="w-full h-[120%] object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-noir-bg via-noir-bg/20 to-noir-bg/40" />
      </motion.div>

      <motion.div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-24 sm:pb-28" style={{ y: textY, opacity: fade }}>
        <MaskedLine delay={1.9}>
          <span className="font-mono2 text-[10px] sm:text-xs tracking-[0.4em] uppercase text-gold">Est. 2026 — Premium Streetwear</span>
        </MaskedLine>
        <h1 className="font-display font-extrabold uppercase leading-[0.85] tracking-tight mt-5 text-[17vw] sm:text-[13vw] lg:text-[10rem]">
          <MaskedLine delay={2.0}><span className="text-cream">Jae</span></MaskedLine>
          <MaskedLine delay={2.1}><span className="text-outline">Noir</span></MaskedLine>
        </h1>
        <MaskedLine delay={2.3}>
          <p className="mt-6 font-display text-base sm:text-2xl uppercase tracking-[0.2em] text-cream/90">
            Move Different. <span className="text-gold">Leave a Legacy.</span>
          </p>
        </MaskedLine>
        <motion.div
          className="mt-10 flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.6, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <Link to="/shop?drop=new" className="group bg-gold text-noir-bg px-8 py-4 font-mono2 text-[11px] tracking-[0.25em] uppercase flex items-center justify-center gap-3 hover:bg-cream transition-colors duration-300" data-testid="hero-cta-shop-drop">
            Shop the Drop <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link to="/story" className="group border border-cream/30 text-cream px-8 py-4 font-mono2 text-[11px] tracking-[0.25em] uppercase flex items-center justify-center gap-3 hover:border-gold hover:text-gold transition-colors duration-300" data-testid="hero-cta-explore">
            Explore Jae Noir <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-6 right-6 sm:right-10 z-10 font-mono2 text-[9px] tracking-[0.3em] uppercase text-cream/40 flex items-center gap-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3, duration: 1 }}
      >
        Scroll
        <motion.span className="block w-px h-10 bg-gradient-to-b from-gold to-transparent" animate={{ scaleY: [1, 0.4, 1] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }} style={{ originY: 0 }} />
      </motion.div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, link, linkLabel }) {
  return (
    <div className="flex items-end justify-between gap-6 mb-12 sm:mb-16">
      <div>
        <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-5xl mt-4 leading-[0.95]">{title}</h2>
        </Reveal>
      </div>
      {link && (
        <Reveal delay={0.2} className="hidden sm:block">
          <Link to={link} className="group font-mono2 text-[10px] tracking-[0.25em] uppercase text-cream/60 hover:text-gold transition-colors flex items-center gap-2 whitespace-nowrap" data-testid={`section-link-${linkLabel?.toLowerCase().replace(/\s/g, "-")}`}>
            {linkLabel} <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      )}
    </div>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const drop = products.filter((p) => p.drop);
  const featured = products
    .filter((p) => p.featured)
    .sort((a, b) => (a.featured_rank ?? 99) - (b.featured_rank ?? 99));
  const limited = products.filter((p) => p.status === "limited" || p.status === "coming_soon");

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email, phone: phone || undefined });
      toast.success("Welcome to the Noir List.");
      setEmail("");
      setPhone("");
    } catch {
      toast.error("Enter a valid email.");
    }
  };

  return (
    <div data-testid="home-page">
      <Hero />
      <DropBanner />
      <Marquee />

      {/* New Drop */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32" data-testid="new-drop-section">
        <SectionHeader eyebrow="Drop 001 — Founders" title={<>New<br />Drop</>} link="/shop?drop=new" linkLabel="View the Drop" />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {drop.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      </section>

      {/* Featured Pieces — bento */}
      <section className="border-y border-white/10 bg-noir-panel/40" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <SectionHeader eyebrow="Selected Garments" title="Featured Pieces" link="/shop" linkLabel="Shop All" />
          <div className="grid grid-cols-12 gap-4 sm:gap-6">
            {featured[0] && (
              <div className="col-span-12 md:col-span-7">
                <Link to={`/product/${featured[0].slug}`} className="spotlight-card group relative block aspect-[4/3] md:aspect-auto md:h-full overflow-hidden bg-noir-card" data-testid="featured-hero-piece">
                  <img src={img(featured[0].images[0])} alt={featured[0].name} className="w-full h-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-noir-bg/90 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6 sm:p-10">
                    <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-gold mb-2">{featured[0].serial} — {featured[0].badge || "Featured"}</p>
                    <h3 className="font-display text-2xl sm:text-4xl font-bold uppercase">{featured[0].name}</h3>
                  </div>
                </Link>
              </div>
            )}
            <div className="col-span-12 md:col-span-5 grid grid-cols-2 md:grid-cols-1 gap-4 sm:gap-6">
              {featured.slice(1, 3).map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Shop by Category */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32" data-testid="categories-section">
        <SectionHeader eyebrow="The Wardrobe" title="Shop by Category" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.08}>
              <Link to={`/shop?category=${c.name}`} className="group relative block aspect-[3/4] overflow-hidden bg-noir-card" data-testid={`category-tile-${c.name.toLowerCase()}`}>
                <img src={img(c.image)} alt={c.name} loading="lazy" className="w-full h-full object-cover opacity-70 transition-all duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.07] group-hover:opacity-100" />
                <div className="absolute inset-0 bg-gradient-to-t from-noir-bg/90 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 flex items-center justify-between">
                  <span className="font-display text-sm sm:text-base font-bold uppercase tracking-wide">{c.name}</span>
                  <ArrowUpRight size={16} className="text-gold opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-500" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Story teaser — numbered chapters */}
      <section className="border-y border-white/10" data-testid="story-teaser-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <SectionHeader eyebrow="The Jae Noir Story" title={<>Built in Silence.<br />Move in Faith.</>} link="/story" linkLabel="Read the Story" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <Link to="/story" className="group flex gap-6 sm:gap-10 py-8 sm:py-10 border-b border-white/10" data-testid={`chapter-teaser-${c.n}`}>
                  <span className="font-mono2 text-gold/60 text-sm pt-2">{c.n}</span>
                  <div>
                    <h3 className="font-display text-xl sm:text-3xl font-bold uppercase group-hover:text-gold transition-colors duration-300">{c.title}</h3>
                    <p className="text-cream/50 text-sm leading-relaxed mt-3 max-w-lg">{c.text}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Built Different statement */}
      <section className="relative overflow-hidden" data-testid="built-different-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 sm:py-44 text-center">
          <Reveal><Eyebrow className="justify-center">The Standard</Eyebrow></Reveal>
          <h2 className="font-display font-extrabold uppercase leading-[0.9] mt-8">
            <MaskedLine inView><span className="block text-[11vw] sm:text-7xl lg:text-8xl text-cream">Built</span></MaskedLine>
            <MaskedLine inView delay={0.12}><span className="block text-[11vw] sm:text-7xl lg:text-8xl text-outline">Different</span></MaskedLine>
          </h2>
          <Reveal delay={0.25}>
            <p className="mt-10 text-cream/50 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
              Heavyweight fabrics. Hand-finished washes. Embroidery over prints. Every Jae Noir garment is built to outlast the trend cycle — and to mean something while it does.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Limited Releases */}
      <section className="border-y border-white/10 bg-noir-panel/40" data-testid="limited-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <SectionHeader eyebrow="Numbered & Never Restocked Twice" title="Limited Releases" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {limited.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link to={`/product/${p.slug}`} className="group block border border-white/10 hover:border-gold/40 transition-colors duration-500 p-6" data-testid={`limited-card-${p.slug}`}>
                  <div className="flex justify-between items-start mb-8">
                    <span className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-gold">{p.serial}</span>
                    <span className={`font-mono2 text-[9px] tracking-[0.2em] uppercase px-2 py-1 ${p.status === "limited" ? "bg-gold text-noir-bg" : "border border-gold/50 text-gold"}`}>{p.badge}</span>
                  </div>
                  <div className="aspect-square overflow-hidden bg-noir-card mb-6">
                    <img src={img(p.images[0])} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-[1.06]" />
                  </div>
                  <h3 className="font-display text-base font-bold uppercase group-hover:text-gold transition-colors">{p.name}</h3>
                  <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-cream/40 mt-2">
                    {p.units > 0 ? `${p.units} units` : <DropCountdown />}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial / culture */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32" data-testid="editorial-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
          <Reveal className="lg:col-span-5">
            <div className="aspect-[3/4] overflow-hidden bg-noir-card">
              <img src={img("lookbook-2.jpg")} alt="Jae Noir editorial" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:pl-10">
            <Reveal><Eyebrow>More Than a Store</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-5xl leading-[0.95] mt-4">
                A culture for the ones who <span className="text-gold">work quietly</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-cream/50 text-sm sm:text-base leading-relaxed max-w-lg">
                Jae Noir is worn in early-morning gyms, late-night studios, and every quiet hour in between. The lookbook isn't marketing — it's documentation of the people the brand was built for.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link to="/lookbook" className="inline-flex items-center gap-3 mt-10 border border-cream/30 px-8 py-4 font-mono2 text-[10px] tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors duration-300" data-testid="editorial-lookbook-link">
                View Lookbook <ArrowRight size={13} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Noir List */}
      <section id="noir-list" className="relative border-t border-white/10 overflow-hidden" data-testid="noir-list-section">
        <div className="absolute inset-0">
          <img src={img("lookbook-1.jpg")} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-noir-bg via-noir-bg/60 to-noir-bg" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-40 text-center">
          <Reveal><Eyebrow>Early Access. Private Drops.</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-6xl mt-6">Join the Noir List</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-cream/50 text-sm sm:text-base max-w-md mx-auto">
              Drop dates, restocks, and members-only releases. No noise — only what matters.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <form onSubmit={subscribe} className="mt-10 max-w-md mx-auto" data-testid="noir-list-form">
              <div className="flex">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="flex-1 bg-noir-bg/60 backdrop-blur border border-white/15 border-r-0 px-5 py-4 text-sm placeholder:text-cream/30 focus:outline-none focus:border-gold/60 transition-colors"
                  data-testid="noir-list-email-input"
                />
                <button type="submit" className="bg-gold text-noir-bg px-6 sm:px-8 font-mono2 text-[10px] tracking-[0.25em] uppercase hover:bg-cream transition-colors duration-300" data-testid="noir-list-submit-button">
                  Join
                </button>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone for SMS drop alerts — optional (+1…)"
                className="w-full mt-2 bg-noir-bg/60 backdrop-blur border border-white/15 px-5 py-3.5 text-sm placeholder:text-cream/30 focus:outline-none focus:border-gold/60 transition-colors"
                data-testid="noir-list-phone-input"
              />
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
